import api from "./axios";
import {
  AutoScheduleResult,
  BoardColumn,
  BoardColumnRequest,
  ProjectTask,
  ProjectTaskRequest,
  TaskScheduleResponse,
  TimesheetTaskOption,
  UpdateProjectTaskRequest,
} from "../interfaces/ProjectTask";

const BASE = "/ProjectTask";
const COLUMNS_BASE = "/BoardColumn";

// ── Colunas do board (kanban) ────────────────────────────────────────────────

/** Colunas/etapas do board, ordenadas por posição (inclui customizadas como "Blocked"). */
export async function getBoardColumns(): Promise<BoardColumn[]> {
  const { data } = await api.get(COLUMNS_BASE);
  return Array.isArray(data) ? data : [];
}

export async function createBoardColumn(req: BoardColumnRequest): Promise<BoardColumn> {
  const { data } = await api.post(COLUMNS_BASE, req);
  return data;
}

export async function updateBoardColumn(id: number, req: BoardColumnRequest): Promise<BoardColumn> {
  const { data } = await api.put(`${COLUMNS_BASE}/${id}`, req);
  return data;
}

export async function deleteBoardColumn(id: number): Promise<void> {
  await api.delete(`${COLUMNS_BASE}/${id}`);
}

/** Reordena as colunas (ids na nova ordem). */
export async function reorderBoardColumns(orderedIds: number[]): Promise<void> {
  await api.put(`${COLUMNS_BASE}/reorder`, { orderedIds });
}

export async function getTasksByProject(projectId: number): Promise<ProjectTask[]> {
  const { data } = await api.get(`${BASE}/project/${projectId}`);
  return Array.isArray(data) ? data : [];
}

/**
 * Lista tarefas com filtros opcionais por cliente e/ou projeto.
 * Sem filtros, retorna todas as tarefas (de todos os clientes/projetos) que o usuário pode ver.
 */
export async function getTasks(
  clientId?: number,
  projectId?: number
): Promise<ProjectTask[]> {
  const { data } = await api.get(`${BASE}`, {
    params: { clientId, projectId },
  });
  return Array.isArray(data) ? data : [];
}

export async function getTask(id: number): Promise<ProjectTask> {
  const { data } = await api.get(`${BASE}/${id}`);
  return data;
}

export async function createTask(req: ProjectTaskRequest): Promise<number> {
  const { data } = await api.post(`${BASE}`, req);
  return data?.id;
}

export async function updateTask(req: UpdateProjectTaskRequest): Promise<void> {
  await api.put(`${BASE}`, req);
}

export async function updateTaskStatus(
  id: number,
  status: number,
  orderIndex?: number
): Promise<void> {
  await api.patch(`${BASE}/${id}/status`, { status, orderIndex });
}

export async function deleteTask(id: number): Promise<void> {
  await api.delete(`${BASE}/${id}`);
}

/** Gera/recalcula o cronograma a partir das estimativas e do capacity diário. */
export async function autoSchedule(projectId?: number): Promise<AutoScheduleResult> {
  const { data } = await api.post(`${BASE}/auto-schedule`, null, {
    params: { projectId },
  });
  return data;
}

/** Cronograma dia-a-dia (horas planejadas por dia) de uma tarefa. */
export async function getTaskSchedule(taskId: number): Promise<TaskScheduleResponse> {
  const { data } = await api.get(`${BASE}/${taskId}/schedule`);
  return data;
}

/** Cronogramas (em lote) das tarefas no escopo cliente/projeto — para o Gantt. */
export async function getSchedules(
  clientId?: number,
  projectId?: number
): Promise<TaskScheduleResponse[]> {
  const { data } = await api.get(`${BASE}/schedules`, { params: { clientId, projectId } });
  return Array.isArray(data) ? data : [];
}

/** Horas realizadas (timesheet) por tarefa — para estimado × realizado. */
export async function getWorkedByTask(
  clientId?: number,
  projectId?: number
): Promise<{ taskId: number; workedHours: number }[]> {
  const { data } = await api.get(`${BASE}/worked`, { params: { clientId, projectId } });
  return Array.isArray(data) ? data : [];
}

/** Substitui o cronograma de uma tarefa (define quantas horas em cada dia). */
export async function updateTaskSchedule(
  taskId: number,
  lines: { date: string; plannedHours: number }[]
): Promise<TaskScheduleResponse> {
  const { data } = await api.put(`${BASE}/${taskId}/schedule`, { lines });
  return data;
}

/**
 * Tarefas do portal atribuídas ao colaborador logado num projeto, com o cronograma planejado,
 * para a tela de lançamento de horas puxar e apontar as horas realizadas.
 */
export async function getTasksForTimesheet(
  projectId: number
): Promise<TimesheetTaskOption[]> {
  const { data } = await api.get(`${BASE}/for-timesheet`, {
    params: { projectId },
  });
  return Array.isArray(data) ? data : [];
}
